import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../Header/constants';
import LogoutConfirmModal from '../Header/LogoutConfirmModal';
import './ProfileNavigation.css';

const IconProfileDropdown = () => (
  <svg className="profile-dropdown-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" opacity="0.4" />
    <path d="M20 21.0002V19.0002C20 16.7902 18.21 15.0002 16 15.0002H8C5.79 15.0002 4 16.7902 4 19.0002V21.0002" />
  </svg>
);

const IconSettingsDropdown = () => (
  <svg className="profile-dropdown-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" opacity="0.4" />
    <path d="M19.6 12.0002C19.6 11.6402 19.56 11.2902 19.5 10.9502L21.54 9.3702C21.73 9.2202 21.78 8.9502 21.66 8.7302L19.66 5.2702C19.54 5.0502 19.27 4.9602 19.05 5.0502L16.56 6.1102C16.04 5.6502 15.47 5.2602 14.85 4.9402L14.49 2.1802C14.46 1.9502 14.25 1.7902 14.02 1.7902H9.98C9.75 1.7902 9.54 1.9502 9.51 2.1802L9.15 4.9402C8.53 5.2602 7.96 5.6502 7.44 6.1102L4.95 5.0502C4.73 4.9602 4.46 5.0502 4.34 5.2702L2.34 8.7302C2.21 8.9502 2.27 9.2202 2.46 9.3702L4.5 10.9502C4.44 11.2902 4.4 11.6402 4.4 12.0002C4.4 12.3602 4.44 12.7102 4.5 13.0502L2.46 14.6302C2.27 14.7802 2.21 15.0502 2.34 15.2702L4.34 18.7302C4.46 18.9502 4.73 19.0402 4.95 18.9502L7.44 17.8902C7.96 18.3502 8.53 18.7402 9.15 19.0602L9.51 21.8202C9.54 22.0502 9.75 22.2102 9.98 22.2102H14.02C14.25 22.2102 14.46 22.0502 14.49 21.8202L14.85 19.0602C15.47 18.7402 16.04 18.3502 16.56 17.8902L19.05 18.9502C19.27 19.0402 19.54 18.9502 19.66 18.7302L21.66 15.2702C21.78 15.0502 21.73 14.7802 21.54 14.6302L19.5 13.0502C19.56 12.7102 19.6 12.3602 19.6 12.0002Z" />
  </svg>
);

const IconLogoutDropdown = () => (
  <svg className="profile-dropdown-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8.9 7.5602C9.21 3.9602 11.06 2.4902 15.11 2.4902H15.24C19.71 2.4902 21.5 4.2802 21.5 8.7502V15.2702C21.5 19.7402 19.71 21.5302 15.24 21.5302H15.11C11.09 21.5302 9.24 20.0802 8.91 16.5402" opacity="0.4" />
    <path d="M15 12H3.62M5.85 8.6502L2.5 12.0002L5.85 15.3502" />
  </svg>
);

const ProfileNavigation = ({
  activeSection,
  onSectionChange,
  variant = 'sidebar', // 'sidebar', 'dropdown', or 'tabs'
  onNavigate, // Callback when navigation happens (for closing dropdown)
  onLogoutClick // Callback for logout click (used in dropdown mode)
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isLoggingOut, currentUser, userProfile } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const accountHref = `${ROUTES.PROFILE}?section=account`;
  const homeHref = ROUTES.PROFILE;
  const bookingsHref = `${ROUTES.PROFILE}?section=bookings`;
  const venueListsHref = ROUTES.VENUE_LISTS || '/venue-lists';

  const handleSectionLinkClick = (section) => {
    if ((variant === 'sidebar' || variant === 'tabs') && onSectionChange) {
      onSectionChange(section);
    }
    if (variant === 'dropdown' && onNavigate) {
      onNavigate();
    }
  };

  const handleLogoutClick = () => {
    if (variant === 'dropdown' && onLogoutClick) {
      onLogoutClick();
      return;
    }
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    navigate('/');
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const getActiveSectionFromURL = () => {
    if (location.pathname.startsWith('/venue-lists')) return 'venue-lists';
    if (location.pathname !== ROUTES.PROFILE) return null;
    const section = new URLSearchParams(location.search).get('section');
    if (section === 'bookings') return 'bookings';
    if (section === 'account') return 'account';
    return 'home';
  };

  const currentSection = (variant === 'sidebar' || variant === 'tabs')
    ? activeSection
    : getActiveSectionFromURL();

  const isHomeActive = currentSection === 'home';
  const isAccountDetailsActive = currentSection === 'account';
  const isBookingsActive = currentSection === 'bookings';
  const isVenueListsActive = currentSection === 'venue-lists';

  const displayName =
    [userProfile?.first_name || userProfile?.firstName, userProfile?.last_name || userProfile?.lastName]
      .filter(Boolean)
      .join(' ') ||
    currentUser?.displayName ||
    (currentUser?.email ? currentUser.email.split('@')[0] : 'User');

  const userEmail = userProfile?.email || currentUser?.email || '';
  const avatarUrl = currentUser?.photoURL || userProfile?.photo_url || userProfile?.photoUrl || null;
  const avatarInitial = displayName.charAt(0).toUpperCase();

  const getBaseClass = () => {
    if (variant === 'tabs') return 'profile-tab-item';
    if (variant === 'dropdown') return 'profile-dropdown-item';
    return 'profile-nav-item';
  };

  const baseClass = getBaseClass();
  const activeClass = 'active';
  const iconClass = variant === 'tabs'
    ? 'profile-tab-icon'
    : variant === 'dropdown'
      ? 'profile-dropdown-icon'
      : 'profile-nav-icon';

  if (variant === 'dropdown') {
    return (
      <div className="profile-dropdown-menu">
        <div className="profile-dropdown-user">
          <div className="profile-dropdown-avatar">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : avatarInitial}
          </div>
          <div className="profile-dropdown-user-text">
            <p className="profile-dropdown-name">{displayName}</p>
            {userEmail ? <p className="profile-dropdown-email">{userEmail}</p> : null}
          </div>
        </div>

        <div className="profile-dropdown-divider" aria-hidden="true" />

        <Link
          className={`profile-dropdown-item ${isHomeActive ? activeClass : ''}`}
          to={homeHref}
          onClick={() => handleSectionLinkClick('home')}
        >
          <IconProfileDropdown />
          <span>View profile</span>
        </Link>
        <Link
          className={`profile-dropdown-item ${isAccountDetailsActive ? activeClass : ''}`}
          to={accountHref}
          onClick={() => handleSectionLinkClick('account')}
        >
          <IconSettingsDropdown />
          <span>Settings</span>
        </Link>
        <button
          type="button"
          className="profile-dropdown-item profile-dropdown-item--logout"
          onClick={handleLogoutClick}
          disabled={isLoggingOut}
        >
          <IconLogoutDropdown />
          <span>Logout</span>
        </button>
      </div>
    );
  }

  // For tabs variant, only show core profile sections (no logout)
  if (variant === 'tabs') {
    return (
      <>
        <Link
          className={`${baseClass} ${isHomeActive ? activeClass : ''}`}
          to={homeHref}
          onClick={() => handleSectionLinkClick('home')}
          aria-label="Home"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={iconClass}>
            <path d="M3 9.5L12 3L21 9.5V20C21 20.55 20.55 21 20 21H15V14H9V21H4C3.45 21 3 20.55 3 20V9.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Home</span>
        </Link>
        <Link
          className={`${baseClass} ${isAccountDetailsActive ? activeClass : ''}`}
          to={accountHref}
          onClick={() => handleSectionLinkClick('account')}
          aria-label="Settings"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={iconClass}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Settings</span>
        </Link>
        <Link
          className={`${baseClass} ${isBookingsActive ? activeClass : ''}`}
          to={bookingsHref}
          onClick={() => handleSectionLinkClick('bookings')}
          aria-label="My bookings"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={iconClass}>
            <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Bookings</span>
        </Link>
        <Link
          className={`${baseClass} ${isVenueListsActive ? activeClass : ''}`}
          to={venueListsHref}
          onClick={() => handleSectionLinkClick('venue-lists')}
          aria-label="Venue lists"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={iconClass}>
            <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Lists</span>
        </Link>
      </>
    );
  }

  // For sidebar variant
  return (
    <>
      <Link
        className={`${baseClass} ${isHomeActive ? activeClass : ''}`}
        to={homeHref}
        onClick={() => handleSectionLinkClick('home')}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={iconClass}>
          <path d="M3 9.5L12 3L21 9.5V20C21 20.55 20.55 21 20 21H15V14H9V21H4C3.45 21 3 20.55 3 20V9.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Home</span>
      </Link>
      <Link
        className={`${baseClass} ${isAccountDetailsActive ? activeClass : ''}`}
        to={accountHref}
        onClick={() => handleSectionLinkClick('account')}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={iconClass}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Settings</span>
      </Link>
      <Link
        className={`${baseClass} ${isBookingsActive ? activeClass : ''}`}
        to={bookingsHref}
        onClick={() => handleSectionLinkClick('bookings')}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={iconClass}>
          <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>My bookings</span>
      </Link>
      <Link
        className={`${baseClass} ${isVenueListsActive ? activeClass : ''}`}
        to={venueListsHref}
        onClick={() => handleSectionLinkClick('venue-lists')}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={iconClass}>
          <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Venue lists</span>
      </Link>
      <button
        className={`${baseClass} logout`}
        onClick={handleLogoutClick}
        disabled={isLoggingOut}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={iconClass}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Logout</span>
      </button>

      {variant !== 'dropdown' && (
        <LogoutConfirmModal
          isOpen={showLogoutModal}
          onClose={handleCancelLogout}
          onConfirm={handleConfirmLogout}
          isLoggingOut={isLoggingOut}
        />
      )}
    </>
  );
};

export default ProfileNavigation;
